import { useQuery } from "@tanstack/react-query";

import { ICON_STROKE, TrendingUp } from "@/shared/components/icons";
import { fetchProfileSummary } from "@/shared/api";
import { shortDate } from "@/shared/format";
import type { ClosedLeak } from "@/shared/models";

/** "Leaks you've closed" — patterns that showed up in an earlier leak report
 *  but are gone from the latest one. Turns the leak report into evidence of
 *  growth. Hidden until at least one leak has been closed. */
export function ClosedLeaks() {
  const { data } = useQuery({ queryKey: ["profile-summary"], queryFn: fetchProfileSummary });
  const closed = data?.leaks.closed ?? [];
  if (closed.length === 0) return null;

  return (
    <section className="rounded-xl border border-green-500/25 bg-green-500/[0.04] p-5 space-y-3">
      <div className="flex items-center gap-2">
        <TrendingUp size={16} strokeWidth={ICON_STROKE} className="text-green-400" />
        <h2 className="text-sm font-semibold tracking-tight text-text">
          Leaks you've closed
        </h2>
      </div>
      <ul className="space-y-2.5">
        {closed.map((leak) => (
          <ClosedLeakRow key={leak.skillId} leak={leak} />
        ))}
      </ul>
    </section>
  );
}

function ClosedLeakRow({ leak }: { leak: ClosedLeak }) {
  return (
    <li className="flex items-start gap-3">
      <span className="mt-0.5 text-[10px] font-bold uppercase tracking-wider text-green-400 bg-green-500/15 rounded px-1.5 py-0.5 flex-shrink-0">
        Closed
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-sm text-text">{leak.name ?? leak.title}</div>
        <div className="text-xs text-text-muted/70">
          Flagged {shortDate(leak.flaggedAt)} → cleared {shortDate(leak.clearedBy)}
        </div>
      </div>
    </li>
  );
}
